import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  contentQueueService,
  locationAtRiskFlagsService,
  placementAuditsService,
  proofMetricsService,
  reviewDueService,
} from '@/lib/services';
import { listUserAccessReviewCases } from '@/lib/services/supabase/user-access-review';
import { reviewCaseCounts } from '@/lib/customer-ops/user-access-review';
import type { CustomerOpsNavCounts } from '@/components/customer-ops/CustomerOpsSubNav';
import { isActionableAtRiskFlag, isPriorityAtRiskFeedSignal } from '@/lib/customer-ops/at-risk-ui';
import { countPriorityReviewDueRows } from '@/lib/customer-ops/review-due-priority';
import { PLACEMENT_AUDIT_OPEN_STATUSES } from '@/types/customer-ops';
import { proofQueryKeys } from '@/lib/proof-system/query-keys';

const useMock = import.meta.env.VITE_USE_MOCK === 'true';

export function useCustomerOpsNavCounts({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): {
  counts: CustomerOpsNavCounts;
  sidebarBadge: number;
  isLoading: boolean;
} {
  const queryEnabled = enabled && !useMock;
  const { data: reviewDueRows = [], isLoading: reviewDueLoading } = useQuery({
    queryKey: ['review-due'],
    queryFn: () => reviewDueService.list(),
    enabled: queryEnabled,
  });

  const { data: atRiskFlags = [], isLoading: atRiskLoading } = useQuery({
    queryKey: ['at-risk-flags'],
    queryFn: () => locationAtRiskFlagsService.list(),
    enabled: queryEnabled,
  });

  const { data: contentRows = [], isLoading: contentLoading } = useQuery({
    queryKey: ['content-queue'],
    queryFn: () => contentQueueService.list(),
    enabled: queryEnabled,
  });

  const { data: placementAudits = [], isLoading: placementLoading } = useQuery({
    queryKey: ['placement-audits'],
    queryFn: () => placementAuditsService.list(),
    enabled: queryEnabled,
  });

  const { data: proofCounts, isLoading: proofLoading } = useQuery({
    queryKey: proofQueryKeys.aggregates('navigation'),
    queryFn: () => proofMetricsService.navigationCounts(),
    enabled: queryEnabled,
  });

  const { data: accessReviewCases = [], isLoading: accessReviewLoading } = useQuery({
    queryKey: ['user-access-review-cases-nav'],
    queryFn: async () => {
      try {
        return await listUserAccessReviewCases({ environment: 'production' });
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
    retry: false,
    enabled: queryEnabled,
  });

  const counts = useMemo<CustomerOpsNavCounts>(() => {
    const accessReviewFilterCounts = reviewCaseCounts(accessReviewCases);
    return {
      reviewDueOpen: countPriorityReviewDueRows(reviewDueRows),
      atRiskOpen: atRiskFlags.filter(
        (row) => isActionableAtRiskFlag(row.status) && isPriorityAtRiskFeedSignal(row.signal_type)
      ).length,
      contentDrafts: contentRows.filter((row) => row.status === 'draft').length,
      placementAuditsOpen: placementAudits.filter((row) => PLACEMENT_AUDIT_OPEN_STATUSES.includes(row.status))
        .length,
      proofTotal: proofCounts?.total ?? 0,
      proofDrafts: proofCounts?.proofDrafts ?? 0,
      proofSuggestions: proofCounts?.suggestions ?? 0,
      accessReviewsActionable:
        accessReviewFilterCounts.needs_send + accessReviewFilterCounts.reminder_due,
    };
  }, [reviewDueRows, atRiskFlags, contentRows, placementAudits, proofCounts, accessReviewCases]);

  const sidebarBadge = useMemo(
    () =>
      (counts.reviewDueOpen ?? 0) +
      (counts.atRiskOpen ?? 0) +
      (counts.contentDrafts ?? 0) +
      (counts.placementAuditsOpen ?? 0) +
      (counts.proofSuggestions ?? 0) +
      (counts.accessReviewsActionable ?? 0),
    [counts]
  );

  return {
    counts,
    sidebarBadge,
    isLoading:
      reviewDueLoading ||
      atRiskLoading ||
      contentLoading ||
      placementLoading ||
      proofLoading ||
      accessReviewLoading,
  };
}
